import {clsx} from 'clsx';
import {isValidElement, memo} from 'react';

import {Size} from '#theme';
import {Button} from '#ui/atoms/button/index.js';
import type {ButtonProps} from '#ui/atoms/button/index.js';
import type {Icons} from '#ui/atoms/icon/icons.js';
import {Icon} from '#ui/atoms/icon/index.js';
import {Link} from '#ui/atoms/link/index.js';

import * as styles from './style.css.js';

export type ListButtonProps = Omit<ButtonProps, 'asChild'> & {
  selected?: boolean;
  href?: string;
  target?: string;
  rel?: string;
  hardLink?: boolean;
};

export const ListButton = memo(function ListButton({
  selected = false,
  className,
  size,
  disabled,
  href,
  target,
  rel,
  icon,
  hardLink,
  children,
  ...buttonProps
}: ListButtonProps) {
  const classes = clsx(
    styles.listButton,
    {
      [styles.listButtonSelected]: selected,
      [styles.listButtonLarge]: size === Size.Four,
      [styles.listButtonDisabled]: disabled,
    },
    className
  );

  if (href) {
    const iconElem =
      !icon || isValidElement(icon) ? (
        icon
      ) : (
        <Icon
          name={icon as Icons}
          // eslint-disable-next-line react/forbid-component-props
          style={{
            '--color': '"inherit"',
          }}
        />
      );

    return (
      <Button {...buttonProps} asChild size={size} className={classes}>
        <Link
          href={href}
          target={target}
          rel={rel}
          hardLink={hardLink}
          aria-disabled={disabled}
        >
          {iconElem && <span>{iconElem}</span>}
          {children && <span>{children}</span>}
        </Link>
      </Button>
    );
  }

  return (
    <Button
      {...buttonProps}
      size={size}
      disabled={disabled}
      icon={icon}
      className={classes}
    >
      {children}
    </Button>
  );
});

ListButton.displayName = 'ListButton';
